/**
 * src/pages/Login.tsx
 *
 * ログインは2段階:
 *   1) メールアドレス + パスワード
 *   2) サーバーが決めた追加認証（パスキー or メール6桁コード）。選択肢を出すのではなく、
 *      サーバーが「このユーザーに必要な方式」だけを返す。画面から方式は変えられない。
 *
 * ★ must_change_password 検出: 完了後、初期PW変更が必要なら /account?force_change_password=1 へ
 * ★ 追加認証の初期設定が必要なら /account?mfa_setup=1 へ
 */

import { useCallback, useEffect, useState } from 'react'
import { mfaFetch } from '../api/mfa'
import MfaChallenge from '../components/mfa/MfaChallenge'
import { RecoveryTokenForm } from '../components/mfa/useReauth'

const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://mangaos.studio-nekonomiya.workers.dev/api'

type MfaStep = {
  method: 'passkey' | 'email'
  role: string
  emailMasked: string | null
  codeSent: boolean
  sendError: string | null
  resendAfter: number
}

const inputStyle: React.CSSProperties = {
  display: 'block', width: '100%', boxSizing: 'border-box',
  height: 46, padding: '0 14px', fontSize: 16,
  background: 'var(--paper)', color: 'var(--ink)',
  border: '1.5px solid var(--paper-3)', borderRadius: 8,
  outline: 'none', appearance: 'none',
}

function goAfterLogin(user: { must_change_password?: boolean } | undefined, setupRequired: boolean) {
  if (user?.must_change_password) {
    window.location.href = '/account?force_change_password=1'
  } else if (setupRequired) {
    window.location.href = '/account?mfa_setup=1'
  } else {
    window.location.href = '/'
  }
}

export default function Login() {
  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [error,    setError]    = useState<string | null>(null)
  const [loading,  setLoading]  = useState(false)
  const [mfa,      setMfa]      = useState<MfaStep | null>(null)
  const [checking, setChecking] = useState(true)
  const [showRecovery, setShowRecovery] = useState(false)

  // 追加認証待ちのままページを開き直した場合は、続きの画面を出す
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/me`, { credentials: 'include' })
        const data = await res.json().catch(() => ({}))
        if (res.status === 401 && (data as any).mfa_pending) {
          const st = await mfaFetch('/auth/mfa/status')
          if (st.method === 'passkey' || st.method === 'email') {
            setMfa({
              method: st.method, role: st.role, emailMasked: st.email_masked ?? null,
              codeSent: false, sendError: null, resendAfter: 0,
            })
          }
        } else if (res.ok && (data as any).authenticated) {
          window.location.replace('/')
          return
        }
      } catch { /* 通常のログイン画面を出す */ }
      setChecking(false)
    })()
  }, [])

  const reset = useCallback(async (message?: string) => {
    await fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {})
    setMfa(null)
    setShowRecovery(false)
    setPassword('')
    setError(message ?? null)
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method:      'POST',
        headers:     { 'Content-Type': 'application/json' },
        credentials: 'include',
        body:        JSON.stringify({ email, password }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        setError((data as any).error ?? 'ログインに失敗しました')
        return
      }

      const data = await res.json().catch(() => ({})) as any

      // 追加認証が必要 → サーバーが指定した方式の画面へ（方式はクライアントで選べない）
      if (data.mfa_required && data.mfa) {
        setPassword('')
        setMfa({
          method: data.mfa.method,
          role: data.user?.role ?? '',
          emailMasked: data.mfa.email_masked ?? null,
          codeSent: !!data.mfa.code_sent,
          sendError: data.mfa.send_error ?? null,
          resendAfter: data.mfa.resend_after_sec ?? 0,
        })
        return
      }

      goAfterLogin(data.user, !!data.mfa_setup_required)
    } catch {
      setError('サーバーに接続できませんでした')
    } finally {
      setLoading(false)
    }
  }

  const card = (children: React.ReactNode) => (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--paper)',
      padding: '20px 16px',
      boxSizing: 'border-box',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 380,
        background: 'var(--paper-2)',
        border: '1px solid var(--paper-3)',
        borderRadius: 14,
        padding: 'clamp(28px, 7vw, 44px) clamp(20px, 7vw, 36px)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
      }}>
        {/* ロゴ */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{
            fontFamily: 'var(--font-mono)', fontWeight: 700,
            fontSize: 24, letterSpacing: '0.08em', color: 'var(--ink)',
          }}>
            MangaOS
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6 }}>
            管理画面
          </div>
        </div>
        {children}
      </div>
    </div>
  )

  if (checking) {
    return card(<div style={{ textAlign: 'center', color: 'var(--ink-3)', fontSize: 13, padding: '24px 0' }}>確認中...</div>)
  }

  // ── 第2段階: 追加認証 ───────────────────────────────────
  if (mfa) {
    return card(
      <div>
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>追加の本人確認</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
            パスワードは確認できました。もうひと手順です。
          </div>
        </div>

        {showRecovery ? (
          <div>
            <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.7, marginBottom: 14 }}>
              パスキーを使えない管理者向けの復旧手順です。システム保守担当者（Cloudflare を管理する人）が発行した、
              <strong>使い捨て・15分有効</strong>の登録コードを入力すると、既存のパスキーが無効化され、新しいパスキーを登録できます。
            </div>
            <RecoveryTokenForm onSuccess={() => { window.location.href = '/account?mfa_setup=1' }} />
            <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 12 }} onClick={() => setShowRecovery(false)}>
              ← 戻る
            </button>
          </div>
        ) : (
          <>
            <MfaChallenge
              method={mfa.method}
              mode="login"
              emailMasked={mfa.emailMasked}
              initialSent={mfa.codeSent}
              initialSendError={mfa.sendError}
              initialResendAfter={mfa.resendAfter}
              onSuccess={(data) => goAfterLogin(data?.user, false)}
              onSessionLost={() => void reset('追加認証に失敗したため、最初からやり直してください。')}
            />
            <div style={{ borderTop: '1px solid var(--paper-3)', marginTop: 20, paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => void reset()}>
                ← 最初からやり直す
              </button>
              {mfa.method === 'passkey' && (
                mfa.role === 'admin'
                  ? <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => setShowRecovery(true)}>
                      パスキーが使えない（紛失した）場合
                    </button>
                  : <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.7 }}>
                      パスキーが使えない場合は、管理者に連絡してください。認証方式を再設定してもらえます。
                    </div>
              )}
            </div>
          </>
        )}
      </div>
    )
  }

  // ── 第1段階: ID・パスワード ─────────────────────────────
  return card(
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>
          メールアドレス
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="admin@example.com"
          required
          autoComplete="email"
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 24 }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>
          パスワード
        </label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          required
          autoComplete="current-password"
          style={inputStyle}
        />
      </div>

      {error && (
        <div role="alert" style={{
          background: 'rgba(192,57,43,0.08)',
          border: '1px solid rgba(192,57,43,0.25)',
          borderRadius: 8, padding: '10px 14px',
          fontSize: 13, color: 'var(--red)',
          marginBottom: 20,
        }}>
          ⚠ {error}
        </div>
      )}

      <button
        type="submit"
        className="btn btn-primary"
        style={{
          display: 'block', width: '100%',
          height: 48, fontSize: 15, fontWeight: 600,
          borderRadius: 10,
        }}
        disabled={loading}
      >
        {loading ? 'ログイン中...' : 'ログイン'}
      </button>
    </form>
  )
}
